import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Heading,
  Text,
  useToast,
  HStack,
  Select,
  SimpleGrid,
  Checkbox,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { apiUrl, allowStripeBypass } from "../config";
import Navbar from "../components/Navbar";
import { UserAuth } from "../contexts/AuthContext";
import SubmissionSuccess from "../components/SubmissionSuccess";
import PhoneNumberInput from "../components/PhoneNumberInput";

const ORGANIZATION_CATEGORIES = [
  { value: "school", label: "School" },
  { value: "religious", label: "Religious Organization" },
  { value: "sports", label: "Sports Team/Club" },
  { value: "nonprofit", label: "Nonprofit Organization" },
  { value: "community", label: "Community Group" },
  { value: "youth", label: "Youth Organization" },
  { value: "arts", label: "Arts & Culture Organization" },
  { value: "health", label: "Health Organization" },
  { value: "education", label: "Educational Organization" },
  { value: "charity", label: "Charitable Organization" },
  { value: "other", label: "Other" },
];

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Organization name is required"),
  category: Yup.string().required("Organization category is required"),
  otherCategory: Yup.string().when("category", {
    is: "other",
    then: () =>
      Yup.string().required("Please specify the organization category"),
  }),
  description: Yup.string()
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description must not exceed 500 characters")
    .required("Description is required"),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("Province / State is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
    .required("Phone number is required"),
});

const OrganizationForm = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = UserAuth();

  // Pre-populate contact info from the logged-in user where we have it.
  // Phone number isn't stored on the user doc, so it stays blank.
  const initialValues = {
    name: "",
    email: user?.email || "",
    phoneNumber: "",
    category: "",
    otherCategory: "",
    description: "",
    city: "",
    stateOrProvince: "",
    skipApproval: false,
  };

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (values, actions) => {
    try {
      const idToken = sessionStorage.getItem("idToken");
      await axios.post(
        `${apiUrl}/addOrganization`,
        {
          name: values.name,
          email: values.email,
          phoneNumber: values.phoneNumber,
          category:
            values.category === "other"
              ? values.otherCategory
              : values.category,
          description: values.description,
          city: values.city,
          stateOrProvince: values.stateOrProvince,
          // Send a combined `location` string so any older code that reads it
          // continues to render a human-friendly value.
          location: `${values.city}, ${values.stateOrProvince}`,
          userId: user.uid,
          userFirstName: user.firstName,
          userLastName: user.lastName,
          userEmail: user.email,
          skipApproval: allowStripeBypass && values.skipApproval,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      actions.setSubmitting(false);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error creating organization: ", error);
      actions.setSubmitting(false);
      toast({
        title: "Error",
        description: "Failed to create organization. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Navbar />
      <Box minH="calc(100vh - 60px)" bg="primaryBackground" pt="60px" px={4}>
        <VStack
          align="center"
          maxWidth="800px"
          margin="0 auto"
          pt={8}
          spacing={8}
        >
          {!isSubmitted && (
            <Heading as="h1" size="lg" textAlign="center" color="heading">
              Create a new Organization
            </Heading>
          )}

          {isSubmitted ? (
            <SubmissionSuccess />
          ) : (
            <Box
              width="100%"
              bg="secondaryBackground"
              p={8}
              borderRadius="xl"
              boxShadow="md"
            >
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {(props) => (
                  <Form>
                    <VStack spacing={6} width="100%" align="stretch">
                      <Field name="name">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.name && form.touched.name
                            }
                            isRequired
                          >
                            <FormLabel size="sm" htmlFor="name">
                              Organization Name
                            </FormLabel>
                            <Input
                              {...field}
                              id="name"
                              placeholder="Enter organization name"
                            />
                            <FormErrorMessage>
                              {form.errors.name}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      <Field name="category">
                        {({ field, form }) => (
                          <FormControl
                            isRequired
                            isInvalid={
                              form.errors.category && form.touched.category
                            }
                          >
                            <FormLabel size="sm">
                              Organization Category
                            </FormLabel>
                            <Select
                              {...field}
                              placeholder="Select organization category"
                            >
                              {ORGANIZATION_CATEGORIES.map((category) => (
                                <option
                                  key={category.value}
                                  value={category.value}
                                >
                                  {category.label}
                                </option>
                              ))}
                            </Select>
                            <FormErrorMessage>
                              {form.errors.category}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {props.values.category === "other" && (
                        <Field name="otherCategory">
                          {({ field, form }) => (
                            <FormControl
                              isRequired
                              isInvalid={
                                form.errors.otherCategory &&
                                form.touched.otherCategory
                              }
                            >
                              <FormLabel size="sm">
                                Specify Category
                              </FormLabel>
                              <Input
                                {...field}
                                placeholder="Please specify your organization category"
                              />
                              <FormErrorMessage>
                                {form.errors.otherCategory}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      )}

                      <Field name="description">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.description &&
                              form.touched.description
                            }
                            isRequired
                          >
                            <FormLabel size="sm" htmlFor="description">
                              Organization Description
                            </FormLabel>
                            <Input
                              {...field}
                              id="description"
                              as="textarea"
                              placeholder="Describe your organization"
                              minHeight="120px"
                              p={2}
                              resize="vertical"
                            />
                            <FormErrorMessage>
                              {form.errors.description}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      <SimpleGrid
                        columns={{ base: 1, sm: 2 }}
                        spacing={4}
                        width="100%"
                      >
                        <Field name="city">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.city && form.touched.city
                              }
                              isRequired
                            >
                              <FormLabel size="sm" htmlFor="city">
                                City
                              </FormLabel>
                              <Input
                                {...field}
                                id="city"
                                placeholder="Enter city"
                              />
                              <FormErrorMessage>
                                {form.errors.city}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="stateOrProvince">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.stateOrProvince &&
                                form.touched.stateOrProvince
                              }
                              isRequired
                            >
                              <FormLabel size="sm" htmlFor="stateOrProvince">
                                Province / State
                              </FormLabel>
                              <Input
                                {...field}
                                id="stateOrProvince"
                                placeholder="Enter province or state"
                              />
                              <FormErrorMessage>
                                {form.errors.stateOrProvince}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </SimpleGrid>

                      <Field name="email">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.email && form.touched.email
                            }
                            isRequired
                          >
                            <FormLabel size="sm" htmlFor="email">
                              Email
                            </FormLabel>
                            <Input
                              {...field}
                              id="email"
                              type="email"
                              placeholder="Enter email address"
                            />
                            <FormErrorMessage>
                              {form.errors.email}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      <Field name="phoneNumber">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.phoneNumber &&
                              form.touched.phoneNumber
                            }
                            isRequired
                          >
                            <FormLabel size="sm" htmlFor="phoneNumber">
                              Phone Number
                            </FormLabel>
                            <PhoneNumberInput
                              field={field}
                              id="phoneNumber"
                            />
                            <FormErrorMessage>
                              {form.errors.phoneNumber}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {allowStripeBypass && (
                        <Field name="skipApproval">
                          {({ field, form }) => (
                            <FormControl>
                              <Checkbox
                                id="skipApproval"
                                isChecked={field.value}
                                onChange={(e) =>
                                  form.setFieldValue(
                                    "skipApproval",
                                    e.target.checked
                                  )
                                }
                                colorScheme="orange"
                              >
                                Skip approval (testing only)
                              </Checkbox>
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Creates the organization pre-approved.
                                Available in dev/staging only; ignored in
                                production.
                              </Text>
                            </FormControl>
                          )}
                        </Field>
                      )}

                      <HStack justify="space-between" width="100%" pt={4}>
                        <Button
                          variant="ghost"
                          onClick={() => navigate(-1)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          type="submit"
                          isLoading={props.isSubmitting}
                        >
                          Create Organization
                        </Button>
                      </HStack>
                    </VStack>
                  </Form>
                )}
              </Formik>
            </Box>
          )}
        </VStack>
      </Box>
    </>
  );
};

export default OrganizationForm;
